import React from 'react';
import yaml from 'js-yaml';
// renderers is imported separately so Markdoc isn't sent to the client
import Markdoc, {renderers} from '@markdoc/markdoc'

import {getSchema} from '/opt/buildhome/repo/node_modules/@markdoc/next.js/src/runtime.js';
/**
 * Schema is imported like this so end-user's code is compiled using build-in babel/webpack configs.
 * This enables typescript/ESnext support
 */
const config = {};
import * as tags from '/opt/buildhome/repo/markdoc/tags.js'
import * as nodes from '/opt/buildhome/repo/markdoc/nodes.js'
const functions = {};
const schema = {
tags: tags ? (tags.default || tags) : {},
nodes: nodes ? (nodes.default || nodes) : {},
functions: functions ? (functions.default || functions) : {},
...(config ? (config.default || config) : {}),
};

/**
 * Source will never change at runtime, so parse happens at the file root
 */
const source = "---\ntitle: Showcase\npageTitle: Mendable Examples - Chat based search for your docs, website or anything.\ndescription: Answer developer questions automatically with Mendable. Deployable anywhere with one line of code.\n---\n\n\nCheck out some of Mendable's examples to see how you can use it in your own projects. Start out by looking at the Mendable Floating Button component in a [sandbox environment](https://codesandbox.io/s/inspiring-gagarin-r8rg4c?file=/src/App.js).\n\nTemplates, examples and SDKs: \n\n- [Mendable Next.js Starter Template](#mendable-next.js-starter-template)\n- [Slack Bot](#slack-bot)\n- [Docusaurus](#docusaurus)\n- [Python SDK](#python-sdk)\n- [Component Sandbox](#sandbox)\n\n\n\n## Mendable Next.js Starter Template\n\nThis is a sample project to demonstrate how to use Mendable API to build your own custom chatbot interface. Made with [Mendable](https://mendable.ai?ref=nextjs), [Next.js](https://vercel.com/solutions/nextjs), [Vercel AI SDK](https://vercel.com/blog/introducing-the-vercel-ai-sdk) and [shadcn/ui](https://ui.shadcn.com/).\n\nGitHub Repository: [https://github.com/mendableai/mendable-nextjs-chatbot](https://github.com/mendableai/mendable-nextjs-chatbot)\n\n![](/images/readmeHero.png)\n\n## Slack Bot\nSpectroMate is an API server with extended functionality designed for Slack integration in the form of a bot. You can use SpectroMate to handle slash commands, and message actions. You can also use SpectroMate to handle non-slack-related events by creating API endpoints for other purposes.\n\nSpectroMate comes with out-of-the-box support for Mendable. You can use your Mendable-trained model to answer documentation-related questions by using the `/docs` ask `<question>` slash command.\n\nGitHub Repository: [https://github.com/spectrocloud/spectromate/](https://github.com/spectrocloud/spectromate/)\n\n![](/images/spectro.png)\n\n## Docusaurus\n\nDocusaurus is a modern static website generator, primarly used for documentation. You can add Mendable components to any Docusaurus website.\n\n[View documentation](/integrations/docusaurus)\n\n![](/images/langchain.png)\n\n\n## Python SDK\n\nThe Python SDK is a wrapper around the Mendable API. It allows you to easily integrate Mendable into your Python applications.\n\n[View documentation](/sdks/docusaurus)\n[View repository](https://github.com/mendableai/mendable-py)\n\n\n![](/images/python.png)\n\n\n## Sandbox\n\nTest out the Mendable [components](/components/overview) in a sandbox environment.\n\nLink: [https://codesandbox.io/s/inspiring-gagarin-r8rg4c?file=/src/App.js](https://codesandbox.io/s/inspiring-gagarin-r8rg4c?file=/src/App.js)\n";
const filepath = "/examples.md";
const ast = Markdoc.parse(source);

/**
 * Like the AST, frontmatter won't change at runtime, so it is loaded at file root.
 * This unblocks future features, such a per-page dataFetchingFunction.
 */
const frontmatter = ast.attributes.frontmatter
  ? yaml.load(ast.attributes.frontmatter)
  : {};

const {components, ...rest} = getSchema(schema)

export async function getStaticProps(context) {
  const partials = {};

  // Ensure Node.transformChildren is available
  Object.keys(partials).forEach((key) => {
    partials[key] = Markdoc.parse(partials[key]);
  });

  const cfg = {
    ...rest,
    variables: {
      ...(rest ? rest.variables : {}),
      // user can't override this namespace
      markdoc: {frontmatter},
      // Allows users to eject from Markdoc rendering and pass in dynamic variables via getServerSideProps
      ...(context.variables || {})
    },
    partials,
    source,
  };

  /**
   * transform must be called in dataFetchingFunction to support server-side rendering while
   * accessing variables on the server
   */
  const content = Markdoc.transform(ast, cfg);

  return {
    // Removes undefined
    props: JSON.parse(
      JSON.stringify({
        markdoc: {
          content,
          frontmatter,
          file: {
            path: filepath
          }
        },
      })
    ),
  };
}

export default function MarkdocComponent(props) {
  // Only execute HMR code in development
  return renderers.react(props.markdoc.content, React, {
    components: {
      ...components,
      // Allows users to override default components at runtime, via their _app
      ...props.components,
    },
  });
}
